import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, ButtonGroup, Grid, IconButton, Stack } from '@mui/material';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import React, { useEffect } from 'react';
import RGL, { WidthProvider } from 'react-grid-layout';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  fileUploadToS3,
  getInvoiceTemplate,
  getPresignedURLForPut,
  InvoiceTemplateDTO,
  saveFile,
  saveInvoiceTemplate,
} from 'wisestep-api';
import { WsButton, WsColors, WsTitleBanner, WsTypography } from 'wisestep-base';

import { WsAccordion } from '../../../components/filters/components';
import { useDialog } from '../../../hooks/useDialog';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import { InvoicePreview } from './invoicePreview';

const ReactGridLayout = WidthProvider(RGL);

export enum InvoiceComponent {
  Logo = 'Logo',
  Favicon = 'Favicon',
  EntityLegalName = 'EntityLegalName',
  EntityRegisteredAddress = 'EntityRegisteredAddress',
  EntityCorporateAddress = 'EntityCorporateAddress',
  EntityLegalIdentifier = 'EntityLegalIdentifier',
  EntityBankingInformation = 'EntityBankingInformation',
  ClientLegalName = 'ClientLegalName',
  ClientAddress = 'ClientAddress',
  ClientPaymentTerms = 'ClientPaymentTerms',
  ClientLegalIdentifier = 'ClientLegalIdentifier',
  InvoiceType = 'InvoiceType',
  InvoiceNumber = 'InvoiceNumber',
  InvoiceDate = 'InvoiceDate',
  InvoiceDueDate = 'InvoiceDueDate',
  InvoiceDescription = 'InvoiceDescription',
  InvoiceComputation = 'InvoiceComputation',
  AmountInWords = 'AmountInWords',
  AmountInNumbers = 'AmountInNumbers',
  CustomFields = 'CustomFields',
  TAndC = 'TAndC',
  AdditionalComments = 'AdditionalComments',
  FreeText = 'FreeText',
  Address = 'Address',
}

export type InvoiceTemplate = InvoiceTemplateDTO;

const invoiceTemplateDefaultValues: InvoiceTemplate = {
  layout: [],
  logoLogo: false,
  logoFavicon: false,
  logoWatermark: false,
  organizationEntityName: false,
  organizationEntityAddress: false,
  organizationEntityLegal: false,
  organizationEntityBank: false,
  clientName: false,
  clientAddress: false,
  clientLegal: false,
  informationInvoiceNumber: false,
  informationInvoiceDate: false,
  informationInvoiceDueDate: false,
  descriptionCandidateName: false,
  descriptionCandidateCompensation: false,
  descriptionCandidateFeeArrangement: false,
  descriptionCandidateEmail: false,
  computationInvoiceTaxation: false,
  computationInvoiceExtras: false,
  computationAmountInWords: false,
  computationAmountInNumbers: false,
  customFieldsCustomField: false,
  ourEntityIds: [],
  invoiceTypeIds: [],
  type: 'DRAG_DROP',
};

export const InvoiceTemplateForm = ({
  defaultValues = invoiceTemplateDefaultValues,
}: {
  defaultValues?: InvoiceTemplate;
}) => {
  const locationState = useLocation().state as { readonly?: boolean };
  const id = useParams().id;
  const readonly = !!locationState?.readonly;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openDialog] = useDialog();
  const form = useForm<InvoiceTemplate>({ defaultValues });
  const { setValue } = form;
  const values = form.watch();
  const navigate = useNavigate();

  const addComponentToLayout = (component) => {
    setValue('layout', [
      ...values.layout,
      { x: 0, y: 0, w: 4, h: 1, i: `${component}_${uuidv4()}` },
    ]);
  };

  const Preview = () => (
    <InvoicePreview
      template={values}
      invoice={{
        ourEntity: {},
        ourEntityBank: {},
        ourEntityLegalIdentifier: [],
        client: {},
        invoiceTypeName: 'Invoice',
      }}
    />
  );

  useEffect(() => {
    (async () => {
      if (id) {
        const { data } = await getInvoiceTemplate(id);
        form.reset(data);
      }
    })();
  }, [form, id]);

  const onSubmit = async (data) => {
    try {
      const blob = await pdf(<Preview />).toBlob();
      const file = new File([blob], 'template.pdf', {
        type: 'application/pdf',
      });
      const {
        data: { preSignedUrl },
      } = await getPresignedURLForPut({ fileName: file.name, fileType: file.type });
      await fileUploadToS3(file, preSignedUrl);
      const location = preSignedUrl.split('?')[0];
      await saveFile({ location, name: file.name });
      const {
        data: { id: newId },
      } = await saveInvoiceTemplate({
        ...data,
        pdfLocation: location,
      });
      setNotification(success(`Invoice Template has been saved successfully !!`));
      navigate(`/invoice/template/${newId}`);
    } catch (e) {
      setNotification(failed('Something went wrong !!'));
    }
  };

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        sx={{ backgroundColor: WsColors.TheBlues25 }}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Grid container alignItems="flex-start">
          <Grid item xs={12}>
            <WsTitleBanner title="Invoice Template Creation" />
          </Grid>
          <Grid container columnSpacing={4} item xs={12} px={4} py={2}>
            <Grid item xs={7.5}>
              <Box sx={{ backgroundColor: WsColors.BorderGrey }}>
                <ReactGridLayout
                  rowHeight={32}
                  verticalCompact={false}
                  className="layout"
                  onLayoutChange={(layout) => setValue('layout', layout)}
                >
                  {values.layout.map((component) => (
                    <div
                      key={component.i}
                      data-grid={{ ...component, ...(readonly && { static: true }) }}
                      style={{ backgroundColor: 'white', padding: '0.25rem' }}
                    >
                      <Stack direction="row" alignItems="center">
                        <WsTypography bold>
                          {
                            InvoiceComponentGroups.map((group) => group.components)
                              .flat()
                              .find((component1) => component1.key === component.i.split('_')[0])
                              ?.label
                          }
                        </WsTypography>
                        {!readonly && (
                          <IconButton
                            sx={{ m: -1 }}
                            onClick={() =>
                              setValue(
                                'layout',
                                values.layout.filter((item) => item.i !== component.i)
                              )
                            }
                          >
                            <CloseIcon sx={{ color: WsColors.GreyBlack }} />
                          </IconButton>
                        )}
                      </Stack>
                    </div>
                  ))}
                </ReactGridLayout>
              </Box>
              <Stack direction="row" mt={2} justifyContent="flex-end" spacing={2}>
                <WsButton variant="text" onClick={() => navigate(-1)}>
                  Cancel
                </WsButton>
                <WsButton
                  variant="outlined"
                  onClick={() => {
                    openDialog({
                      children: (
                        <PDFViewer height={1024}>
                          <Preview />
                        </PDFViewer>
                      ),
                    });
                  }}
                >
                  Preview
                </WsButton>
                <WsButton type="submit" disabled={readonly}>
                  Save Template
                </WsButton>
              </Stack>
            </Grid>
            {!readonly && (
              <Grid container item xs={4.5}>
                <Stack>
                  {InvoiceComponentGroups.map((group, index) => (
                    <WsAccordion key={index} title={group.name}>
                      <ButtonGroup
                        fullWidth
                        variant="text"
                        aria-label="vertical outlined button group"
                      >
                        {group.components.map((component) => (
                          <Button
                            onClick={() => addComponentToLayout(component.key)}
                            key={component.key}
                          >
                            {component.title}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </WsAccordion>
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
};

const InvoiceComponentGroups = [
  {
    name: 'Logo',
    components: [
      { key: InvoiceComponent.Logo, title: 'Logo', label: 'Logo' },
      { key: InvoiceComponent.Favicon, title: 'Favicon', label: 'Favicon' },
    ],
  },
  {
    name: 'Organization Entity',
    components: [
      { key: InvoiceComponent.EntityLegalName, title: 'Legal Name', label: 'Entity Legal Name' },
      {
        key: InvoiceComponent.EntityRegisteredAddress,
        title: 'Registered Address',
        label: 'Entity Registered Address',
      },
      {
        key: InvoiceComponent.EntityCorporateAddress,
        title: 'Corporate Address',
        label: 'Entity Corporate Address',
      },
      {
        key: InvoiceComponent.EntityLegalIdentifier,
        title: 'Legal Identifier',
        label: 'Entity Legal Identifier',
      },
      {
        key: InvoiceComponent.EntityBankingInformation,
        title: 'Banking Information',
        label: 'Entity Banking Information',
      },
    ],
  },
  {
    name: 'Client Entity',
    components: [
      { key: InvoiceComponent.ClientLegalName, title: 'Legal Name', label: 'Client Legal Name' },
      { key: InvoiceComponent.ClientAddress, title: 'Client Address', label: 'Client Address' },
      {
        key: InvoiceComponent.ClientPaymentTerms,
        title: 'Payment Terms',
        label: 'Client Payment Terms',
      },
      {
        key: InvoiceComponent.ClientLegalIdentifier,
        title: 'Legal Identifier',
        label: 'Client Legal Identifier',
      },
    ],
  },
  {
    name: 'Invoice Information',
    components: [
      { key: InvoiceComponent.InvoiceType, title: 'Invoice Type', label: 'Invoice Type' },
      { key: InvoiceComponent.InvoiceNumber, title: 'Invoice Number', label: 'Invoice Number' },
      { key: InvoiceComponent.InvoiceDate, title: 'Invoice Date', label: 'Invoice Date' },
      {
        key: InvoiceComponent.InvoiceDueDate,
        title: 'Invoice Due Date',
        label: 'Invoice Due Date',
      },
    ],
  },
  {
    name: 'Invoice Description',
    components: [
      {
        key: InvoiceComponent.InvoiceDescription,
        title: 'Invoice Description',
        label: 'Invoice Description',
      },
    ],
  },
  {
    name: 'Invoice Computation',
    components: [
      {
        key: InvoiceComponent.InvoiceComputation,
        title: 'Invoice Computation',
        label: 'Invoice Computation',
      },
      { key: InvoiceComponent.AmountInWords, title: 'Amount in Words', label: 'Amount in Words' },
      {
        key: InvoiceComponent.AmountInNumbers,
        title: 'Amount in Numbers',
        label: 'Amount in Numbers',
      },
    ],
  },
  {
    name: 'Custom Fields',
    components: [
      { key: InvoiceComponent.CustomFields, title: 'Custom Fields', label: 'Custom Fields' },
    ],
  },
  {
    name: 'Terms and Conditions',
    components: [
      { key: InvoiceComponent.TAndC, title: 'Terms and Conditions', label: 'Terms and Conditions' },
    ],
  },
  {
    name: 'Description/Comments',
    components: [
      {
        key: InvoiceComponent.AdditionalComments,
        title: 'Description/Comments',
        label: 'Description/Comments',
      },
    ],
  },
  {
    name: 'Free text at the bottom',
    components: [{ key: InvoiceComponent.FreeText, title: 'Free Text', label: 'Free Text' }],
  },
  {
    name: 'Address / Phone number',
    components: [{ key: InvoiceComponent.Address, title: 'Address', label: 'Address' }],
  },
];
